"use client";

import React, { useState, useEffect, useRef } from "react";
import { Minus, Plus, Calculator, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
export default function InstantInvestmentEstimator() {
  const [sqFt, setSqFt] = useState<number>(500);
  const [systemType, setSystemType] = useState<string>("flake");
  const [extraColors, setExtraColors] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [displayedTotal, setDisplayedTotal] = useState<number>(0);
  const PRICE_FLAKE = 8;
  const PRICE_METALLIC = 12;
  const PRICE_EXTRA_COLOR = 59.99;
  useEffect(() => {
    const basePrice = systemType === "metallic" ? PRICE_METALLIC : PRICE_FLAKE;
    const calculatedTotal = sqFt * basePrice + extraColors * PRICE_EXTRA_COLOR;
    setTotal(calculatedTotal);
  }, [sqFt, systemType, extraColors]);
  useEffect(() => {
    let start = displayedTotal;
    const end = total;
    if (start === end) return;
    const duration = 1000;
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      const current = start + (end - start) * ease;
      setDisplayedTotal(current);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [total]);
  const handleSqFtChange = (val: number) => {
    setSqFt(val);
  };
  const handleSystemChange = (val: string) => {
    if (val) setSystemType(val);
  };
  const incrementColors = () => setExtraColors(prev => Math.min(prev + 10, prev + 1));
  const decrementColors = () => setExtraColors(prev => Math.max(0, prev - 1));
  return <section id="instant-investment-estimator" className="dark bg-[hsl(0,0%,5%)] text-foreground py-12 px-5 md:py-32 md:px-16 relative overflow-hidden border-y border-white/10">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center mb-10 md:mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs font-mono tracking-widest uppercase text-white/70 mb-4">
            <Calculator className="w-3 h-3" />
            <span>Pricing Calculator</span>
          </div>
          <h2 className="text-xl md:text-5xl font-sans font-light tracking-tight text-white">
            Instant Investment <span className="font-serif italic text-white/80">Estimator</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm leading-7 md:text-lg font-light">
            Calculate the estimated cost of your luxury epoxy flooring project. 
            Select your system, area size, and customization options.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          {}
          <div className="space-y-6 bg-card/30 rounded-xl border border-white/10 backdrop-blur-sm ![padding:22px_20px_22px_20px] !w-[275px] lg:col-span-7 md:space-y-10">
            
            {}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base md:text-lg font-medium text-white">System Type</Label>
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-mono">
                  {systemType === "flake" ? "$8.00 / sq.ft" : "Starts at $12.00 / sq.ft"}
                </span>
              </div>
              <ToggleGroup type="single" value={systemType} onValueChange={handleSystemChange} className="w-full grid grid-cols-2 gap-4">
                <ToggleGroupItem value="flake" className="h-auto py-3 px-3 md:py-4 md:px-4 border border-white/10 bg-white/5 hover:bg-white/10 data-[state=on]:bg-white data-[state=on]:text-black data-[state=on]:border-white transition-all duration-300 flex flex-col gap-2 items-center justify-center rounded-lg">
                  <span className="font-bold tracking-wide !text-xs">FLAKE SYSTEM</span>
                  <span className="text-xs opacity-70 font-light">Durable & Decorative</span>
                </ToggleGroupItem>
                <ToggleGroupItem value="metallic" className="h-auto py-3 px-3 md:py-4 md:px-4 border border-white/10 bg-white/5 hover:bg-white/10 data-[state=on]:bg-white data-[state=on]:text-black data-[state=on]:border-white transition-all duration-300 flex flex-col gap-2 items-center justify-center rounded-lg">
                  <span className="font-bold tracking-wide !text-xs">METALLIC SYSTEM</span>
                  <span className="text-xs opacity-70 font-light">Luxury & Unique</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {}
            <div className="space-y-6">
              <div className="flex items-end justify-between">
                <Label className="text-base md:text-lg font-medium text-white">Total Area</Label>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl md:text-3xl font-mono font-bold text-white">{sqFt}</span>
                  <span className="text-sm text-muted-foreground font-mono">SQ.FT</span>
                </div>
              </div>
              
              <div className="relative pt-2 pb-2">
                <Input type="number" min={0} max={5000} step={10} value={sqFt} onChange={e => setSqFt(Number(e.target.value))} className="w-full" />
                <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                  <span>0 sq.ft</span>
                  <span>2500 sq.ft</span>
                  <span>5000+ sq.ft</span>
                </div>
              </div>
            </div>

            {}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-base md:text-lg font-medium text-white flex items-center gap-2">
                    Extra Colors
                    <div className="group relative">
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-black border border-white/20 text-xs text-white rounded hidden group-hover:block z-50">
                        Add custom colors to your mix for a unique look.
                      </div>
                    </div>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    ${PRICE_EXTRA_COLOR} per additional color
                  </p>
                </div>
                
                <div className="flex items-center gap-4 bg-white/5 rounded-lg p-1 border border-white/10">
                  <Button variant="ghost" size="icon" onClick={decrementColors} disabled={extraColors <= 0} className="h-10 w-10 rounded-md hover:bg-white/10 text-white disabled:opacity-30">
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-mono text-xl font-bold text-white">
                    {extraColors}
                  </span>
                  <Button variant="ghost" size="icon" onClick={incrementColors} className="h-10 w-10 rounded-md hover:bg-white/10 text-white">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-center h-full">
            <div className="sticky top-24 space-y-8">
              <div className="bg-black border border-white/20 p-6 md:p-8 rounded-2xl shadow-[0_0_50px_-12px_rgba(255,255,255,0.1)] relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                <div className="relative z-10 space-y-2 text-center">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-medium">
                    Estimated Investment
                  </h3>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-3xl md:text-6xl font-bold text-white font-mono tracking-tighter drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                      ${Math.floor(displayedTotal).toLocaleString()}
                    </span>
                    <span className="text-lg md:text-3xl text-white/50 font-mono self-start mt-2">
                      {(displayedTotal % 1).toFixed(2).substring(1)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 max-w-xs mx-auto">
                    *This is a preliminary estimate. Final pricing may vary based on surface condition and specific requirements.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm border-t border-white/10 pt-4">
                  <div className="text-muted-foreground">System:</div>
                  <div className="text-right font-medium text-white capitalize">{systemType}</div>
                  
                  <div className="text-muted-foreground">Area Size:</div>
                  <div className="text-right font-medium text-white">{sqFt} sq.ft</div>
                  
                  <div className="text-muted-foreground">Extra Colors:</div>
                  <div className="text-right font-medium text-white">{extraColors}</div>
                </div>

                
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}